import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { InstagramIcon } from "./InstagramIcon";
import { company, services } from "../data/site";

const serviceTestIds = {
  security: "nav-service-security-link",
  housekeeping: "nav-service-housekeeping-link",
  events: "nav-service-events-link",
  valet: "nav-service-valet-link",
};

const navLinkClass = ({ isActive }) =>
  `text-sm font-medium transition-colors duration-200 ${
    isActive ? "text-sky-600" : "text-slate-700 hover:text-sky-600"
  }`;

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const navigate = useNavigate();

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 h-20 bg-white/85 backdrop-blur-md border-b border-slate-200/80">
      <div className="mx-auto max-w-7xl h-full px-6 lg:px-10 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo" onClick={closeMobile}>
          <img src="/assets/logo-icon.png" alt="STARLINK Security & Facilities logo" className="h-11 w-auto object-contain" />
          <span className="leading-tight">
            <img src="/assets/starlink-wordmark.png" alt="STARLINK" className="h-6 w-auto object-contain" />
            <span className="block font-mono text-[10px] tracking-[0.18em] uppercase text-[#2E3A8C] mt-1">
              Security & Facilities Pvt. Ltd.
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9" data-testid="nav-desktop-menu">
          <NavLink to="/" end className={navLinkClass} data-testid="nav-home-link">
            Home
          </NavLink>

          <div className="relative group">
            <button
              type="button"
              className="text-sm font-medium text-slate-700 group-hover:text-sky-600 transition-colors duration-200 cursor-default"
              data-testid="nav-services-dropdown-trigger"
            >
              <span className="flex items-center gap-1">
                Services
                <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
              </span>
            </button>
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 invisible opacity-0 translate-y-2 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
              <div
                className="w-[22rem] rounded-xl border border-slate-200 bg-white p-2 shadow-[0_12px_32px_rgba(15,23,42,0.12)]"
                data-testid="nav-services-dropdown-menu"
              >
                {services.map((s) => (
                  <button
                    key={s.id}
                    data-testid={serviceTestIds[s.id]}
                    onClick={() => navigate(`/services/${s.id}`)}
                    className="w-full text-left rounded-lg px-4 py-3 hover:bg-slate-50 transition-colors duration-200 group/item"
                  >
                    <span className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-900">{s.title}</span>
                      <ArrowUpRight className="h-4 w-4 text-slate-300 group-hover/item:text-sky-600 transition-colors duration-200" />
                    </span>
                    <span className="block text-xs text-slate-500 mt-0.5">{s.tagline}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <NavLink to="/clients" className={navLinkClass} data-testid="nav-clients-link">
            Clients
          </NavLink>
          <NavLink to="/gallery" className={navLinkClass} data-testid="nav-gallery-link">
            Gallery
          </NavLink>
          <NavLink to="/about" className={navLinkClass} data-testid="nav-about-link">
            About Us
          </NavLink>
          <NavLink to="/contact" className={navLinkClass} data-testid="nav-contact-link">
            Contact Us
          </NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <a
            href={company.instagram}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            data-testid="nav-instagram-icon"
            className="text-slate-500 hover:text-sky-600 transition-colors duration-200"
          >
            <InstagramIcon className="h-5 w-5" />
          </a>
          <Link
            to="/contact"
            data-testid="nav-request-proposal-button"
            className="rounded-full bg-[#0F2A4A] px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 transition-colors duration-300"
          >
            Request Proposal
          </Link>
        </div>

        <button
          className="lg:hidden text-slate-800"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-menu-toggle"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="lg:hidden absolute top-20 inset-x-0 bg-white border-b border-slate-200 shadow-[0_12px_32px_rgba(15,23,42,0.12)]"
            data-testid="nav-mobile-drawer"
          >
            <nav className="px-6 py-6 flex flex-col gap-1">
              <NavLink to="/" end onClick={closeMobile} data-testid="mobile-nav-home-link"
                className="py-3 text-base font-medium text-slate-800 border-b border-slate-100">
                Home
              </NavLink>
              <button
                className="py-3 text-base font-medium text-slate-800 border-b border-slate-100 flex items-center justify-between"
                onClick={() => setMobileServicesOpen((v) => !v)}
                data-testid="mobile-nav-services-toggle"
              >
                Services
                <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${mobileServicesOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {mobileServicesOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    {services.map((s) => (
                      <button
                        key={s.id}
                        data-testid={`mobile-nav-service-${s.id}-link`}
                        onClick={() => { closeMobile(); navigate(`/services/${s.id}`); }}
                        className="block w-full text-left pl-4 py-2.5 text-sm text-slate-600 hover:text-sky-600"
                      >
                        {s.title}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
              <NavLink to="/clients" onClick={closeMobile} data-testid="mobile-nav-clients-link"
                className="py-3 text-base font-medium text-slate-800 border-b border-slate-100">
                Clients
              </NavLink>
              <NavLink to="/gallery" onClick={closeMobile} data-testid="mobile-nav-gallery-link"
                className="py-3 text-base font-medium text-slate-800 border-b border-slate-100">
                Gallery
              </NavLink>
              <NavLink to="/about" onClick={closeMobile} data-testid="mobile-nav-about-link"
                className="py-3 text-base font-medium text-slate-800 border-b border-slate-100">
                About Us
              </NavLink>
              <NavLink to="/contact" onClick={closeMobile} data-testid="mobile-nav-contact-link"
                className="py-3 text-base font-medium text-slate-800 border-b border-slate-100">
                Contact Us
              </NavLink>
              <div className="flex items-center justify-between pt-5">
                <a href={company.instagram} aria-label="Instagram" data-testid="mobile-nav-instagram-icon"
                  className="text-slate-500 hover:text-sky-600">
                  <InstagramIcon className="h-5 w-5" />
                </a>
                <Link to="/contact" onClick={closeMobile} data-testid="mobile-nav-request-proposal-button"
                  className="rounded-full bg-[#0F2A4A] px-5 py-2.5 text-sm font-semibold text-white">
                  Request Proposal
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
